import {
  CHEST_SLOTS,
  CRATE_SLOTS,
  GEAR_SLOTS,
  GEAR_SLOT_NAME,
  HOTBAR_SLOTS,
  INVENTORY,
  ITEMS,
  quickMoveTarget,
  slotOf,
  toolCondition,
  type EditTarget,
  type Gear,
  type GearSlot,
  type ItemId,
  type SlotRef,
  type SlotSpec,
  type Slots,
  type Wear,
} from "@dyson/shared";
import { drawItemIcon } from "../render/TileAtlas";

/** One slot's element and what it was last drawn with, so icons are only redrawn when they change. */
interface Cell {
  el: HTMLElement;
  icon: HTMLCanvasElement;
  count: HTMLElement;
  condition: HTMLElement;
  shown: ItemId | null;
}

/** The chest open in the panel: where it is, its slots as the server last said, and whether it's a crate. */
export interface OpenChest {
  target: EditTarget;
  slots: Slots;
  crate: boolean;
}

/**
 * The pack, Minecraft's way (Tab): what you wear down the side, the 27-slot
 * grid, and the hotbar under it. Click a stack to pick it up and click a slot
 * to put it down (onto the same thing it tops up; onto something else the two
 * swap); right click picks up half, or puts down one. Shift+click sends a
 * stack across between the grid and the hotbar, and 1-9 over a slot swaps it
 * with that hotbar slot.
 *
 * A chest (right clicked in the world) opens here too, over the pack: its 27
 * slots, or a cargo crate's one big one. Stacks go between it and the pack the
 * same way, and shift+click sends a stack from one to the other.
 *
 * What's picked up stays in its slot until it's put down (it's only the
 * server's slots that move, see moveAcross), so closing the panel puts it back.
 */
export class PackPanel {
  /** Move `n` from pack slot `from` to `to`; returns whether it moved (see Building.move). */
  onMove: (from: number, to: number, n: number) => boolean = () => false;
  /** Move `n` between the chest and the pack (or about the chest); `to` null is a shift-click across. Returns whether it moved. */
  onChestMove: (from: SlotRef, to: SlotRef | null, n: number) => boolean = () => false;
  onEquip: (item: ItemId) => void = () => {};
  onUnequip: (slot: GearSlot) => void = () => {};

  private readonly root = document.getElementById("pack")!;
  private readonly gearBox = this.root.querySelector<HTMLElement>(".pack-gear")!;
  private readonly grid = this.root.querySelector<HTMLElement>(".pack-grid")!;
  private readonly hotbar = this.root.querySelector<HTMLElement>(".pack-hotbar")!;
  private readonly countLabel = this.root.querySelector<HTMLElement>(".pack-count")!;
  private readonly chestBox = this.root.querySelector<HTMLElement>(".pack-chest")!;
  private readonly chestName = this.root.querySelector<HTMLElement>(".pack-chest-name")!;
  private readonly chestGrid = this.root.querySelector<HTMLElement>(".pack-chest-grid")!;
  private readonly cursor = document.getElementById("pack-cursor")!;
  private readonly cursorIcon = this.cursor.querySelector("canvas")!;
  private readonly cursorCount = this.cursor.querySelector<HTMLElement>(".count")!;
  private readonly cells: Cell[] = [];
  private chestCells: Cell[] = [];
  private readonly gearCells = new Map<GearSlot, Cell>();

  private slots: Slots = [];
  /** The open chest, if any. */
  chest: OpenChest | null = null;
  /** What's picked up: `n` of the stack in slot `from` (of the chest, or the pack). */
  private carrying: { from: SlotRef; n: number } | null = null;
  /** The slot under the mouse, so 1-9 can swap it with a hotbar slot. */
  hovered: SlotRef | null = null;
  private drawn = "";

  constructor() {
    this.root.querySelector(".pack-close")!.addEventListener("click", () => this.close());
    for (const slot of GEAR_SLOTS) {
      const cell = makeCell("pack-slot gear-slot");
      const label = document.createElement("span");
      label.className = "label";
      label.textContent = GEAR_SLOT_NAME[slot];
      cell.el.append(label);
      cell.el.addEventListener("click", (e) => this.clickGear(slot, e));
      this.gearCells.set(slot, cell);
      this.gearBox.append(cell.el);
    }
    for (let i = 0; i < INVENTORY.slots; i++) this.addCell();
    this.root.addEventListener("mouseleave", () => (this.hovered = null));
    // The picked-up stack follows the pointer.
    document.addEventListener("pointermove", (e) => {
      if (this.carrying) this.cursor.style.transform = `translate(${e.clientX - 18}px, ${e.clientY - 18}px)`;
    });
  }

  get isOpen(): boolean {
    return !this.root.classList.contains("hidden");
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.root.classList.remove("hidden");
    this.drawn = "";
  }

  close(): void {
    this.root.classList.add("hidden");
    this.drop();
    this.hovered = null;
    this.setChest(null);
  }

  /** What's in a chest, as the server says: opens the panel on it, or (already open on it) brings it up to date. */
  showChest(chest: OpenChest): void {
    const same = this.chest && sameTarget(this.chest.target, chest.target);
    if (!same) this.drop();
    this.setChest(chest);
    this.root.classList.remove("hidden");
  }

  /** The chest's slots, moved about here before the server says so (see Game). */
  get chestSlots(): Slots {
    return this.chest?.slots ?? [];
  }

  get chestSpec(): SlotSpec {
    return this.chest?.crate ? CRATE_SLOTS : CHEST_SLOTS;
  }

  private setChest(chest: OpenChest | null): void {
    const was = this.chest;
    this.chest = chest;
    this.root.classList.toggle("with-chest", !!chest);
    this.chestBox.classList.toggle("hidden", !chest);
    // A different shape of chest (or none): its slots are made again.
    if (!chest || !was || was.crate !== chest.crate) {
      this.chestCells = [];
      this.chestGrid.replaceChildren();
    }
    if (chest) this.chestName.textContent = chest.crate ? "Cargo crate" : "Chest";
    this.drawn = "";
  }

  /** Redraw if anything changed. `selected`: the hotbar slot in hand. */
  update(slots: Slots, gear: Gear, wear: Wear, selected: number): void {
    if (!this.isOpen) return;
    this.slots = slots;
    // What was picked up may have been used, moved or thrown meanwhile.
    const held = this.carrying && this.stackAt(this.carrying.from);
    if (this.carrying && !held) this.drop();
    else if (this.carrying && held) this.carrying.n = Math.min(this.carrying.n, held.n, INVENTORY.stack);
    const key = JSON.stringify([slots, gear, wear, selected, this.carrying, this.chest?.slots, this.chest?.crate]);
    if (key === this.drawn) return;
    this.drawn = key;

    while (this.cells.length < slots.length) this.addCell();
    this.cells.forEach((cell, i) => {
      const s = i < slots.length ? slots[i] : null;
      this.drawSlot(cell, s ?? null, { chest: false, i }, INVENTORY.slots, wear);
      cell.el.classList.toggle("selected", i === selected);
    });
    if (this.chest) {
      const spec = this.chestSpec;
      const chest = this.chest.slots;
      while (this.chestCells.length < Math.max(spec.size, chest.length)) this.addChestCell();
      this.chestCells.forEach((cell, i) => this.drawSlot(cell, chest[i] ?? null, { chest: true, i }, spec.size, wear));
    }
    for (const [slot, cell] of this.gearCells) {
      const worn = gear[slot] ?? null;
      setCell(cell, worn, worn ? 1 : 0, null);
      cell.count.textContent = "";
      cell.el.classList.toggle("worn", !!worn);
      cell.el.title = worn ? `${ITEMS[worn].name} (${GEAR_SLOT_NAME[slot].toLowerCase()}): click to take it off` : `${GEAR_SLOT_NAME[slot]}: pick up something worn there and click here`;
    }
    const used = slots.filter(Boolean).length;
    this.countLabel.textContent = `${Math.min(used, INVENTORY.slots)}/${INVENTORY.slots}`;
    this.countLabel.classList.toggle("warn", used >= INVENTORY.slots);

    this.cursor.classList.toggle("hidden", !this.carrying);
    if (this.carrying) {
      drawItemIcon(this.stackAt(this.carrying.from)!.item, this.cursorIcon);
      this.cursorCount.textContent = this.carrying.n > 1 ? String(this.carrying.n) : "";
    }
  }

  /** One slot drawn: what's in it, less what's picked up from it; past `size`, overflow (there, but no room for it). */
  private drawSlot(cell: Cell, s: { item: ItemId; n: number } | null, ref: SlotRef, size: number, wear: Wear): void {
    const picked = this.carrying && this.carrying.from.chest === ref.chest && this.carrying.from.i === ref.i ? this.carrying.n : 0;
    const n = (s?.n ?? 0) - picked;
    setCell(cell, s?.item ?? null, n, s && ITEMS[s.item].tier ? toolCondition(s.item, wear) : null);
    cell.el.classList.toggle("picked", picked > 0);
    cell.el.classList.toggle("overflow", ref.i >= size);
    cell.el.classList.toggle("hidden", ref.i >= size && !s);
    const where = ref.chest ? (this.chest?.crate ? "this crate" : "this chest") : "your pack";
    cell.el.title = s ? `${ITEMS[s.item].name} (${s.n})${ref.i >= size ? `: no room for it in ${where}` : ""}` : "";
  }

  private addCell(): void {
    const i = this.cells.length;
    const cell = makeCell("pack-slot");
    this.listen(cell, { chest: false, i });
    this.cells.push(cell);
    (i < HOTBAR_SLOTS ? this.hotbar : this.grid).append(cell.el);
  }

  private addChestCell(): void {
    const i = this.chestCells.length;
    const cell = makeCell("pack-slot" + (this.chest?.crate && i === 0 ? " crate" : ""));
    this.listen(cell, { chest: true, i });
    this.chestCells.push(cell);
    this.chestGrid.append(cell.el);
  }

  private listen(cell: Cell, ref: SlotRef): void {
    cell.el.addEventListener("click", (e) => this.click(ref, e));
    cell.el.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.rightClick(ref);
    });
    cell.el.addEventListener("mouseenter", () => (this.hovered = ref));
  }

  private stackAt(ref: SlotRef): { item: ItemId; n: number } | null {
    return (ref.chest ? this.chestSlots : this.slots)[ref.i] ?? null;
  }

  /** Move `n` from one slot to another, wherever they are. */
  private move(from: SlotRef, to: SlotRef, n: number): boolean {
    if (!from.chest && !to.chest) return this.onMove(from.i, to.i, n);
    return this.onChestMove(from, to, n);
  }

  /** Left click: pick up the whole stack (at most a pack stack's worth, from a crate), or put down what's picked up (shift: send it across). */
  private click(ref: SlotRef, e: MouseEvent): void {
    const s = this.stackAt(ref);
    if (!this.carrying) {
      if (!s) return;
      if (e.shiftKey) {
        // To or from a chest: as much as will go to the other side.
        if (this.chest) return void this.onChestMove(ref, null, s.n);
        // Something wearable, with nothing worn there: shift+click puts it on, as in Minecraft.
        const wornAt = slotOf(s.item);
        if (wornAt && !this.gearCells.get(wornAt)!.shown) return this.onEquip(s.item);
        const to = quickMoveTarget(this.slots, ref.i);
        if (to !== null) this.onMove(ref.i, to, s.n);
        return;
      }
      this.carrying = { from: ref, n: Math.min(s.n, INVENTORY.stack) };
    } else if (sameRef(ref, this.carrying.from)) {
      this.carrying = null;
    } else if (this.move(this.carrying.from, ref, this.carrying.n)) {
      this.carrying = null;
    }
    this.drawn = "";
  }

  /** Right click: pick up half a stack, or put down one of what's picked up. */
  private rightClick(ref: SlotRef): void {
    const s = this.stackAt(ref);
    if (!this.carrying) {
      if (!s) return;
      this.carrying = { from: ref, n: Math.min(Math.ceil(s.n / 2), INVENTORY.stack) };
    } else if (sameRef(ref, this.carrying.from)) {
      // One back where it came from.
      this.carrying.n -= 1;
      if (this.carrying.n < 1) this.carrying = null;
    } else if (this.move(this.carrying.from, ref, 1)) {
      this.carrying.n -= 1;
      if (this.carrying.n < 1) this.carrying = null;
    }
    this.drawn = "";
  }

  /** A gear slot clicked: put on what's picked up from the pack (if it's worn there), or take off what's worn. */
  private clickGear(slot: GearSlot, e: MouseEvent): void {
    e.stopPropagation();
    if (this.carrying) {
      const item = !this.carrying.from.chest ? this.stackAt(this.carrying.from)?.item : undefined;
      if (item && slotOf(item) === slot) {
        this.onEquip(item);
        this.carrying = null;
      }
    } else if (this.gearCells.get(slot)!.shown) this.onUnequip(slot);
    this.drawn = "";
  }

  /** Let go of what's picked up: it never left its slot. */
  private drop(): void {
    this.carrying = null;
    this.cursor.classList.add("hidden");
  }
}

const sameRef = (a: SlotRef, b: SlotRef): boolean => a.chest === b.chest && a.i === b.i;

const sameTarget = (a: EditTarget, b: EditTarget): boolean => a.kind === b.kind && a.id === b.id && a.x === b.x && a.y === b.y;

function makeCell(className: string): Cell {
  const el = document.createElement("div");
  el.className = className;
  const icon = document.createElement("canvas");
  icon.width = icon.height = 32;
  const count = document.createElement("span");
  count.className = "count";
  const condition = document.createElement("span");
  condition.className = "condition";
  el.append(icon, count, condition);
  return { el, icon, count, condition, shown: null };
}

/** Draw `item` (n of it, with a tool's `condition` 0..1 as a bar) in a cell; nothing when n is 0. */
function setCell(cell: Cell, item: ItemId | null, n: number, condition: number | null): void {
  const show = n > 0 ? item : null;
  if (cell.shown !== show) {
    cell.shown = show;
    if (show) drawItemIcon(show, cell.icon);
    else cell.icon.getContext("2d")!.clearRect(0, 0, 32, 32);
  }
  cell.count.textContent = show && n > 1 ? String(n) : "";
  cell.condition.style.display = show && condition !== null ? "block" : "none";
  if (show && condition !== null) {
    cell.condition.style.width = `${Math.round(condition * 100)}%`;
    cell.condition.classList.toggle("low", condition < 0.25);
  }
}
